import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Boxes, Camera, Layers, Trash2 } from "lucide-react";
import React, { Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  createBox,
  printFactoryLabel,
  getBoxesConfig,
  mergeBoxes,
  scanCode,
  type BoxView,
  type ScanItemOut,
  type ScanOut,
} from "@/api/boxes";
import { boxLeftover, groupSscc, itemKey, looksLikeFactoryBox } from "@/lib/boxes";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import {
  CharacteristicChoice,
  NO_CHARACTERISTIC,
  charAnswer,
} from "@/lib/scan-ui/characteristic-choice";
import { useBackGuard } from "@/lib/scan-ui/back-guard";
import { parseScan } from "@/lib/order-scan";
import { markOf } from "@/lib/scan-ui/marks";
import { BottomSheet } from "@/lib/scan-ui/overlays";
import { QtyInput } from "@/lib/scan-ui/qty-input";
import { fmtQty } from "@/lib/scan-ui/status";
import { apiErrorText, useGoBack } from "@/lib/scan-ui/screen";
import { useScanCapture } from "@/lib/use-scan-capture";
import { ScanBanner } from "@/pages/fbs/ScanBanner";

type Banner = { kind: "ok" | "error" | "warn"; text: string } | null;

/**
 * Экран скана раздела «Грузоместа».
 *
 * Один вход на оба случая — в этом и смысл общего резолвера: кладовщик не
 * обязан заранее знать, что он сейчас поднесёт к сканеру.
 *
 * * **скан коробки** (SSCC) — открывается её карточка: там разагрегируют;
 * * **скан короба/паллеты с упаковки** — их карточка (`CargoPage`); в сборку
 *   коробки склада они не идут;
 * * **скан товара** (EAN13 / GS1 / ЧЗ) — позиция копится в «новой коробке»,
 *   которую заводят одной кнопкой. Так собирают короб из россыпи.
 * * **несколько коробок подряд** — их можно собрать в одну (агрегация).
 */
export function BoxScanPage(): React.ReactElement {
  const navigate = useNavigate();
  // Режим приходит с плитки главного экрана и меняет ДВЕ вещи: подпись в шапке
  // и что делать с отсканированной коробкой. Механика скана одна, поэтому
  // отдельных экранов нет — но человек должен видеть, в какую задачу он вошёл.
  const [params] = useSearchParams();
  const mode = params.get("mode") === "take" ? "take"
    : params.get("mode") === "look" ? "look"
    : "merge";
  const qc = useQueryClient();
  const [banner, setBanner] = React.useState<Banner>(null);
  const [items, setItems] = React.useState<ScanItemOut[]>([]);
  // Сырые коды штук ЧЗ, уложенных в новую коробку (по строке состава): уходят
  // в теле создания, и штука из короба упаковки на бэке отмечается вынутой —
  // иначе одна штука числилась бы и в коробе, и в коробке. Сборка по EAN
  // кодов не копит.
  // `mark` — нормальная форма кода (`markOf`): одна штука, отсканированная
  // второй раз (в той же или другой форме), в коробку не кладётся (К6).
  const [pieces, setPieces] = React.useState<{ key: string; raw: string; mark: string }[]>([]);
  const [picked, setPicked] = React.useState<BoxView[]>([]);
  const [last, setLast] = React.useState<ScanOut | null>(null);
  // Уточнение НАКАПЛИВАЕТСЯ: сначала спросили характеристику, потом партию.
  // Неся только последний ответ, экран спрашивал бы предыдущий по кругу —
  // грабли уже собраны инвентаризацией.
  // Разбор ЗАВОДСКОГО короба: у него нет наклейки портала, поэтому объектом
  // операции становится ОСТАТОК — его и заводим коробкой.
  const [factory, setFactory] = React.useState<
    { item: ScanItemOut & { qty_base: number }; taken: number } | null
  >(null);

  const [ask, setAsk] = React.useState<
    { raw: string; char?: string; batch?: string } | null
  >(null);
  const [cameraOpen, setCameraOpen] = React.useState(false);
  // `useGoBack`, а не `navigate("/boxes")`: последнее — push, и аппаратная
  // «назад» кидала бы кладовщика обратно в скан.
  const goBack = useGoBack("/boxes");

  // Чанк камеры тянем ПРИ ВХОДЕ на экран: иначе проверка версии приходится на
  // тап «Сканировать», то есть на середину работы, когда человек уже поднёс
  // телефон к коробу.
  usePreloadChunk(CameraScanner);
  const config = useQuery({ queryKey: ["boxes-config"], queryFn: getBoxesConfig });
  const cameraAllowed =
    config.data?.scan_method === "camera" || config.data?.scan_method === "both";

  // ⚠️ Камеру открываем сразу ТОЛЬКО когда она единственный способ скана.
  //
  // При `both` этот же экран открывают на ТСД, где сканер аппаратный, а
  // открытая камера гасит его перехват (`enabled: !cameraOpen`) — то есть
  // автооткрытие сломало бы скан там, где он работает лучше всего. Вместо
  // автооткрытия при `both` экран показывает КРУПНУЮ кнопку камеры: на
  // телефоне она и есть точка входа, на ТСД её просто не трогают.
  const cameraOnly = config.data?.scan_method === "camera";
  React.useEffect(() => {
    if (cameraOnly) setCameraOpen(true);
  }, [cameraOnly]);
  useBackGuard(cameraOpen, () => setCameraOpen(false));

  const add = React.useCallback((next: ScanItemOut[]) => {
    setItems((cur) => {
      const byKey = new Map(cur.map((i) => [itemKey(i), { ...i }]));
      for (const n of next) {
        const have = byKey.get(itemKey(n));
        if (have) have.qty_base = Number(have.qty_base) + Number(n.qty_base);
        else byKey.set(itemKey(n), { ...n });
      }
      return [...byKey.values()];
    });
  }, []);

  /** Штука ЧЗ уложена в новую коробку — её сырой код уйдёт в `piece_codes`. */
  const rememberPiece = React.useCallback(
    (kind: string, raw: string, item: ScanItemOut) => {
      if (kind !== "datamatrix") return;
      setPieces((cur) => [...cur, { key: itemKey(item), raw, mark: markOf(parseScan(raw)) }]);
    },
    [],
  );

  /** Скан с ТСД/камеры. В сборке повтор той же штуки ЧЗ — отказ до запроса:
   *  иначе количество коробки удвоилось бы, а из короба вынулась одна (К6). */
  const submit = (raw: string) => {
    const mark = mode === "merge" ? markOf(parseScan(raw)) : "";
    if (mark && pieces.some((p) => p.mark === mark)) {
      setBanner({ kind: "error", text: "Эта штука уже в коробке — одна штука кладётся один раз." });
      return;
    }
    scan.mutate({ raw });
  };

  const scan = useMutation({
    mutationFn: (arg: { raw: string; char?: string; batch?: string }) => {
      const p = parseScan(arg.raw);
      return scanCode({
        code: arg.raw,
        scan_kind: p.kind,
        ean13: p.ean13 ?? "",
        gtin: p.gtin ?? "",
        serial: p.serial ?? "",
        // Введённый оператором номер партии перекрывает то, что дал код:
        // на этикетке он напечатан, а в штрихкоде его может не быть.
        batch: arg.batch ?? p.batch ?? "",
        characteristic_ref: arg.char ?? "",
        qty: p.qty ?? null,
      });
    },
    onSuccess: (out, sent) => {
      setLast(out);
      if (out.matched && (out.needs_characteristic || out.needs_partia)) {
        // ⚠️ Ответы НАКАПЛИВАЕМ из того, что реально ушло на сервер (`sent`),
        // а не из прежнего состояния шторки: там ответа ещё нет, и экран
        // спрашивал бы характеристику по кругу — ответил, спросили партию,
        // ответил, снова спросили характеристику.
        setAsk({ raw: sent.raw, char: sent.char, batch: sent.batch });
        return;
      }
      if (out.cargo) {
        // Короб/паллета с упаковки — своя карточка (`CargoPage`), проверяется
        // ДО `matched`: бэк отдаёт её с `matched=false` (в сборку она не идёт).
        setAsk(null);
        const c = out.cargo;
        if (mode === "merge") {
          // ⚠️ Объединять короб упаковки с коробками склада нельзя: его
          // состав — коды штук ЧЗ, связь с ними потерялась бы.
          setBanner({
            kind: "error",
            text:
              `${c.kind === "pallet" ? "Паллета" : "Короб"} с упаковки ${groupSscc(c.code)}` +
              " — объединять с коробками склада нельзя. Открыть её карточку — «Что в грузоместе».",
          });
          return;
        }
        navigate(
          `/boxes/cargo/${c.code}${mode === "take" && c.kind === "box" ? "?take=1" : ""}`,
        );
        return;
      }
      if (!out.matched && out.box && mode !== "merge") {
        // Коробка есть, но в учёт не идёт (отменена — К3): карточку всё равно
        // открываем — по ней видно, что это было и почему наклейку снять.
        setAsk(null);
        navigate(`/boxes/${out.box.id}`);
        return;
      }
      if (!out.matched) {
        setAsk(null);
        setBanner({ kind: "error", text: out.message || "Код не опознан" });
        return;
      }
      setAsk(null);
      if (out.box) {
        // ⚠️ В режимах «взять» и «посмотреть» коробка открывается СРАЗУ, а не
        // копится: там она и есть цель скана. Копить её в список источников
        // нужно только при сборке, где коробок будет несколько.
        if (mode !== "merge") {
          // Намерение едет в карточку: пришли «взять» — степперы нужны сразу,
          // человек за этим и сканировал. Пришли «посмотреть» — карточка
          // остаётся справкой.
          navigate(`/boxes/${out.box.id}${mode === "take" ? "?take=1" : ""}`);
          return;
        }
        setPicked((cur) =>
          cur.some((b) => b.id === out.box!.id) ? cur : [...cur, out.box!],
        );
        setBanner({
          kind: "ok",
          text: `Коробка ${groupSscc(out.box.sscc)} · ${out.items.length} поз.`,
        });
        return;
      }
      // ⚠️ В режимах «взять» и «посмотреть» скан ТОВАРА — не повод заводить
      // коробку. Пока этой ветки не было, режим понимал только наклейку
      // короба, а товарный код проваливался в общую сборку: экран отвечал
      // «Завести коробку (6 шт)» там, где человек собирался разобрать короб.
      //
      // Заводской короб несёт как раз товарный GS1 (GTIN + партия +
      // количество), поэтому текст называет и его: разбор заводского короба —
      // отдельный сценарий, его ещё нет.
      if (mode === "take" && looksLikeFactoryBox(out.items)) {
        // Заводской короб: спрашиваем, сколько забирают, и остаток заводим
        // коробкой. Отказывать здесь нельзя — это ровно та операция, ради
        // которой человек и открыл «Взять из короба».
        setFactory({
          item: out.items[0] as ScanItemOut & { qty_base: number },
          taken: 0,
        });
        setBanner(null);
        return;
      }
      if (mode !== "merge") {
        const first = out.items[0];
        setBanner({
          kind: "warn",
          text:
            `${first?.nomenclature_name || first?.nomenclature_article || "Это товар"}` +
            " — товарный код, а не наклейка короба. Поднесите наклейку с номером" +
            " короба (SSCC). Чтобы собрать из товара новую коробку, вернитесь" +
            " и выберите «Собрать короб».",
        });
        return;
      }
      add(out.items);
      if (out.items.length === 1) rememberPiece(out.kind, sent.raw, out.items[0]!);
      const first = out.items[0];
      setBanner({
        kind: "ok",
        text: `${first?.nomenclature_name || first?.nomenclature_article || "Товар"} · ${
          out.items.reduce((s, i) => s + Number(i.qty_base), 0)
        } шт`,
      });
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });

  const capture = useScanCapture({
    // Пока открыта камера или шторка уточнения, аппаратный перехват молчит:
    // иначе один код пришёл бы дважды (камера + сканер), а ответ на «какая
    // партия» уехал бы в общий разбор вместо поля.
    enabled: !scan.isPending && !cameraOpen && !ask && !factory,
    onScan: submit,
    // ⚠️ Перехват выключен — код НЕ засчитан. Без плашки он пропадал бы молча,
    // и штука считалась бы уложенной в коробку, не будучи уложенной.
    onBlocked: () =>
      setBanner({
        kind: "warn",
        text: scan.isPending
          ? "Предыдущий скан ещё записывается — отсканируйте ещё раз."
          : "Сначала ответьте на вопрос на экране, потом сканируйте.",
      }),
  });

  const create = useMutation({
    mutationFn: (opts: { force?: boolean } = {}) =>
      // ⚠️ Характеристику коробки НЕ шлём: её выводит бэк из состава. Здесь
      // стояла характеристика ПЕРВОЙ позиции — короб из ОЗОН и ВБ подписался
      // бы как ОЗОН, а смешивать их теперь разрешено.
      createBox({
        items: items as (ScanItemOut & { qty_base: number })[],
        force: opts.force,
        piece_codes: pieces.map((p) => p.raw),
      }),
    onSuccess: (box) => {
      setItems([]);
      setPieces([]);
      void qc.invalidateQueries({ queryKey: ["boxes"] });
      // Сразу на карточку: там печатают наклейку, а клеят её немедленно —
      // коробка без наклейки на складе неотличима от любой другой.
      navigate(`/boxes/${box.id}`);
    },
    onError: (e) => {
      // ⚠️ 409 — не поломка, а осмысленный отказ: собрали ПОЛНЫЙ заводской
      // короб, ему свой номер не нужен. Предлагаем то, что нужно на самом
      // деле, — напечатать заводскую этикетку.
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        setFullBox(apiErrorText(e));
        return;
      }
      setBanner({ kind: "error", text: apiErrorText(e) });
    },
  });

  // Полный заводской короб: текст отказа от бэка, он же объясняет человеку,
  // какой это короб («Короб ОПТ, 6 шт»).
  const [fullBox, setFullBox] = React.useState<string | null>(null);

  const factoryLabel = useMutation({
    mutationFn: () =>
      printFactoryLabel({
        items: items as (ScanItemOut & { qty_base: number })[],
        // Штуки из коробов упаковки в заводском коробе — вынуты (К2).
        piece_codes: pieces.map((p) => p.raw),
      }),
    onSuccess: (r) => {
      setFullBox(null);
      setItems([]);
      setPieces([]);
      setBanner({
        kind: "ok",
        text: `Заводская этикетка отправлена на принтер: ${r.unit_name}, ${r.qty} шт.`,
      });
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });

  // Остаток заводского короба → коробка портала. Взятое НЕ пишем: оно уехало
  // товаром, как и при изъятии из портальной коробки.
  const keepLeftover = useMutation({
    mutationFn: () => {
      const left = factory ? boxLeftover(Number(factory.item.qty_base), factory.taken) : null;
      if (!factory || left === null || left <= 0) {
        throw new Error("Нечего оставлять в коробе");
      }
      return createBox({
        items: [{ ...factory.item, qty_base: left }],
        comment: `Остаток заводского короба, забрали ${factory.taken}`,
      });
    },
    onSuccess: (box) => {
      setFactory(null);
      void qc.invalidateQueries({ queryKey: ["boxes"] });
      // Сразу на карточку: там печатают наклейку, и клеить её надо немедленно
      // — короб без наклейки от соседнего не отличить.
      navigate(`/boxes/${box.id}`);
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });

  const merge = useMutation({
    mutationFn: () => mergeBoxes(picked.map((b) => b.id)),
    onSuccess: (box) => {
      setPicked([]);
      void qc.invalidateQueries({ queryKey: ["boxes"] });
      navigate(`/boxes/${box.id}`);
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });

  const total = items.reduce((s, i) => s + Number(i.qty_base || 0), 0);

  return (
    <div className="flex h-app flex-col bg-slate-100">
      <input ref={capture.scanInputRef} {...capture.scanInputProps} className="sr-only" />

      <header className="flex items-center gap-3 bg-white px-3 py-2 shadow-sm">
        <button onClick={goBack} className="text-sm text-slate-500">
          ←
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">
            {mode === "take" ? "Взять из короба"
              : mode === "look" ? "Что в грузоместе"
              : "Собрать короб"}
          </div>
          <div className="text-xs text-slate-500">
            {mode === "merge"
              ? "Сканируйте товар и початые короба"
              : "Поднесите наклейку короба"}
          </div>
        </div>
      </header>

      {banner && (
        <div className="px-3 pt-3">
          <ScanBanner tone={banner.kind} title={banner.text} onClose={() => setBanner(null)} />
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {picked.length > 0 && (
          <section className="mb-3 rounded-lg bg-white p-3 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Boxes className="size-4" /> Отсканированные коробки ({picked.length})
            </div>
            {picked.map((b) => (
              <div key={b.id} className="flex items-center gap-2 border-b py-1.5 last:border-0">
                <button
                  className="min-w-0 flex-1 text-left font-mono text-sm"
                  onClick={() => navigate(`/boxes/${b.id}`)}
                >
                  {groupSscc(b.sscc)}
                </button>
                <span className="text-xs text-slate-500">{b.items.length} поз.</span>
                <button
                  onClick={() => setPicked((c) => c.filter((x) => x.id !== b.id))}
                  className="text-slate-400"
                  aria-label="Убрать"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            ))}
            <Button
              className="mt-2 w-full"
              disabled={picked.length < 2 || merge.isPending}
              onClick={() => merge.mutate()}
            >
              Собрать {picked.length} коробки в одну
            </Button>
            {picked.length === 1 && (
              <p className="mt-1 text-[11px] text-slate-500">
                Для сборки нужна ещё хотя бы одна коробка. Одну — открывайте, чтобы
                разагрегировать.
              </p>
            )}
          </section>
        )}

        {/* ⚠️ Корзина «В новую коробку» — только в сборке. В режимах «взять» и
            «посмотреть» коробку не собирают, и пустая корзина на весь экран
            отвечала на вопрос, которого человек не задавал. */}
        {mode !== "merge" ? (
          <section className="rounded-lg bg-white p-6 text-center shadow-sm">
            <p className="mb-4 text-sm text-slate-600">
              Поднесите наклейку короба сканером
              {cameraAllowed ? " или снимите её камерой" : ""}.
            </p>
            {cameraAllowed && (
              <Button className="w-full" onClick={() => setCameraOpen(true)}>
                <Camera className="mr-2 size-5" />
                Сканировать камерой
              </Button>
            )}
          </section>
        ) : (
        <section className="rounded-lg bg-white p-3 shadow-sm">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium">
            <Layers className="size-4" /> В новую коробку
            {total > 0 ? ` · ${total} шт` : ""}
          </div>
          {items.length === 0 && (
            <p className="py-4 text-center text-sm text-slate-500">
              Сканируйте товар — он будет копиться здесь.
            </p>
          )}
          {items.map((i) => (
            <div key={itemKey(i)} className="flex items-center gap-2 border-b py-1.5 last:border-0">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm">
                  {i.nomenclature_name || i.nomenclature_article || i.nomenclature_ref}
                </div>
                <div className="text-xs text-slate-500">
                  {i.partia_name ? `партия ${i.partia_name}` : "без партии"}
                  {i.characteristic_name ? ` · ${i.characteristic_name}` : ""}
                </div>
              </div>
              <span className="text-sm tabular-nums">{Number(i.qty_base)} шт</span>
              <button
                onClick={() => {
                  setItems((c) => c.filter((x) => itemKey(x) !== itemKey(i)));
                  setPieces((c) => c.filter((p) => p.key !== itemKey(i)));
                }}
                className="text-slate-400"
                aria-label="Убрать"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
          ))}
        </section>
        )}

      </div>

      {fullBox && (
        <BottomSheet onClose={() => setFullBox(null)} panelClassName="p-4">
          <div className="mb-1 text-sm font-semibold">Полный заводской короб</div>
          <p className="mb-3 text-sm text-slate-600">{fullBox}</p>
          <p className="mb-4 text-xs text-slate-500">
            У такого короба есть код групповой упаковки — он сам говорит, что
            внутри и сколько. Свой номер ему не нужен.
          </p>
          <Button
            className="w-full"
            disabled={factoryLabel.isPending}
            onClick={() => factoryLabel.mutate()}
          >
            Напечатать заводскую этикетку
          </Button>
          {/* ⚠️ Выход из тупика. Портал видит СОСТАВ, а не тару: шесть штук в
              заводском коробе и шесть в случайной коробке для него
              неразличимы. Без этой кнопки остаток, случайно совпавший с
              вложенностью, нельзя было ни записать, ни промаркировать. */}
          <Button
            variant="outline"
            className="mt-2 w-full"
            disabled={create.isPending}
            onClick={() => {
              setFullBox(null);
              create.mutate({ force: true });
            }}
          >
            Это не заводской короб — завести коробку
          </Button>
          <Button
            variant="ghost"
            className="mt-2 w-full"
            onClick={() => setFullBox(null)}
          >
            Отмена
          </Button>
        </BottomSheet>
      )}

      {factory && (
        <BottomSheet onClose={() => setFactory(null)} panelClassName="p-4">
          <div className="mb-1 text-sm font-semibold">Заводской короб</div>
          <p className="mb-1 break-words text-sm">
            {factory.item.nomenclature_name || factory.item.nomenclature_article}
          </p>
          <p className="mb-3 text-xs text-slate-500">
            {factory.item.partia_name ? `партия ${factory.item.partia_name} · ` : ""}
            по коду {fmtQty(Number(factory.item.qty_base))} шт
          </p>

          <div className="mb-1 text-sm">Сколько забираете?</div>
          <div className="flex items-center gap-2">
            <QtyInput
              value={factory.taken}
              onChange={(v) => setFactory({ ...factory, taken: v })}
            />
            <Button
              variant="outline"
              onClick={() =>
                setFactory({ ...factory, taken: Number(factory.item.qty_base) })
              }
            >
              Всё
            </Button>
          </div>

          {(() => {
            const total = Number(factory.item.qty_base);
            const left = boxLeftover(total, factory.taken);
            if (left === null) {
              return (
                <p className="mt-3 text-sm text-rose-600">
                  В коробе {fmtQty(total)} шт — больше забрать нельзя.
                </p>
              );
            }
            if (left === 0) {
              return (
                <>
                  <p className="mt-3 text-sm text-slate-600">
                    Забираете короб целиком — заводить в портале нечего,
                    наклейку с него снимите.
                  </p>
                  <Button className="mt-3 w-full" onClick={() => setFactory(null)}>
                    Понятно
                  </Button>
                </>
              );
            }
            return (
              <>
                <p className="mt-3 text-sm text-slate-600">
                  Останется {fmtQty(left)} шт. Короб становится неполным — ему
                  нужен свой номер и наклейка.
                </p>
                <Button
                  className="mt-3 w-full"
                  disabled={factory.taken <= 0 || keepLeftover.isPending}
                  onClick={() => keepLeftover.mutate()}
                >
                  Оставить {fmtQty(left)} шт и напечатать наклейку
                </Button>
              </>
            );
          })()}
        </BottomSheet>
      )}

      {ask && last && (
        <ClarifySheet
          scan={last}
          // ⚠️ Кнопка характеристики здесь ОТПРАВЛЯЕТ скан (когда партия не
          // нужна), а ответ идёт около секунды: без блокировки второй тап
          // уходил вторым сканом — и второй строкой той же номенклатуры.
          busy={scan.isPending}
          onClose={() => setAsk(null)}
          onSubmit={(char, batch) =>
            scan.mutate({
              raw: ask.raw,
              char: char ?? ask.char,
              batch: batch ?? ask.batch,
            })
          }
          onSkipPartia={(char) => {
            // Позиция уходит в черновик как есть — без партии. Экран об этом
            // не молчит: в ленте она видна строкой «без партии».
            if (!last.items.length) return setAsk(null);
            const chosen = char
              ? last.characteristic_options.find((c) => c.ref === char)
              : undefined;
            const accepted =
              last.needs_characteristic && chosen
                ? last.items.map((i) => ({
                    ...i,
                    characteristic_ref: charAnswer(chosen).charRef,
                    characteristic_name: chosen.name,
                  }))
                : last.items;
            add(accepted);
            // Штука ЧЗ без партии — тоже штука из короба упаковки: её код
            // уходит в `piece_codes`, иначе короб числил бы её у себя (К9).
            if (accepted.length === 1) rememberPiece(last.kind, ask.raw, accepted[0]!);
            setAsk(null);
          }}
        />
      )}

      <div className="flex gap-2 border-t bg-white p-3">
        {cameraAllowed && (
          <Button
            variant="outline"
            aria-label="Сканировать камерой"
            onClick={() => setCameraOpen(true)}
          >
            <Camera className="size-5" />
          </Button>
        )}
        {/* ⚠️ «Завести коробку» — только в сборке. В режимах «взять» и
            «посмотреть» коробку не заводят, и кнопка там предлагала ровно то,
            чего человек не собирался делать. */}
        {mode === "merge" ? (
          <Button
            className="flex-1"
            disabled={items.length === 0 || create.isPending}
            onClick={() => create.mutate({})}
          >
            Завести коробку {total > 0 ? `(${total} шт)` : ""}
          </Button>
        ) : (
          <div className="flex flex-1 items-center justify-center text-sm text-slate-500">
            Поднесите наклейку короба
          </div>
        )}
      </div>

      {cameraOpen && (
        <Suspense fallback={null}>
          <div className="fixed inset-0 z-[80] bg-black">
            <CameraScanner
              onDetect={(raw) => {
                setCameraOpen(false);
                submit(raw);
              }}
              onClose={() => setCameraOpen(false)}
            />
          </div>
        </Suspense>
      )}
    </div>
  );
}

/** Ввод номера партии: он напечатан на этикетке, а в штрихкоде его может не быть.
 *
 * ⚠️ `Enter` обязателен: номер партии вводят СКАНЕРОМ с той же этикетки, а
 * сканер шлёт `Enter` в конце кода. Без обработки ввод сканером не работает —
 * это уже учтено в соседних разделах (`InventorySessionPage::PartiaAsk`).
 */
/**
 * Уточнение позиции ОДНИМ экраном: характеристика и партия сразу, как в
 * приёмке возвратов.
 *
 * Раньше вопросы шли по очереди — ответил про характеристику, ушёл запрос,
 * вернулся «а партия?», ответил ещё раз. Оба ответа известны человеку в один
 * момент (он держит товар в руках), и сервер принимает их одним сканом, так
 * что вторая ходка была ценой формы, а не сути.
 *
 * ⚠️ Когда нужна ТОЛЬКО характеристика, кнопка варианта отправляет сразу — там
 * подтверждать нечего, и лишний тап был бы платой за единообразие.
 */
function ClarifySheet({
  scan,
  busy,
  onClose,
  onSubmit,
  onSkipPartia,
}: {
  scan: ScanOut;
  /** Скан уже летит на сервер — кнопки вариантов молчат до ответа. */
  busy: boolean;
  onClose: () => void;
  onSubmit: (char: string | undefined, batch: string | undefined) => void;
  onSkipPartia: (char: string | undefined) => void;
}): React.ReactElement {
  const [char, setChar] = React.useState("");
  const [batch, setBatch] = React.useState("");
  const bothNeeded = scan.needs_characteristic && scan.needs_partia;
  const item = scan.items[0];
  const ready = (!scan.needs_characteristic || char) && batch.trim();

  return (
    <BottomSheet onClose={onClose} panelClassName="p-4">
      <div className="mb-1 text-sm font-semibold">Уточните позицию</div>
      <p className="mb-3 text-xs text-slate-500">
        {item?.nomenclature_article ? `${item.nomenclature_article} · ` : ""}
        {item?.nomenclature_name || scan.raw}
      </p>

      {/* Почему предыдущий ответ не подошёл. Без этой строки шторка
          открывалась второй раз с тем же вопросом, и человек не знал, что его
          номер отвергли: чужую партию из регистра портал намеренно не
          подставляет. */}
      {scan.message && (
        <p className="mb-3 rounded bg-rose-50 px-2 py-1.5 text-xs text-rose-700">
          {scan.message}
        </p>
      )}

      {scan.needs_characteristic && (
        <div className="mb-4">
          <div className="mb-1.5 text-xs font-semibold text-slate-600">
            Характеристика
          </div>
          <div className="space-y-2">
            {/* ⚠️ Ссылку не выдумываем: раньше здесь стояло `c.ref || "none"`,
                то есть пустая ссылка ПРЕВРАЩАЛАСЬ в ответ «никакая». Это
                смешивает «человек не ответил» с «ответил: без характеристики»
                — ровно то различие, ради которого сервер и завёл `none`.
                Пока он всегда шлёт `none`, вреда нет; перестанет — вскроется
                молча. Вариант идёт как есть. */}
            <CharacteristicChoice
              options={scan.characteristic_options}
              answer={{
                charRef: char === NO_CHARACTERISTIC ? "" : char,
                charName: "",
                withoutChar: char === NO_CHARACTERISTIC,
              }}
              onPick={(c) => (bothNeeded ? setChar(c.ref) : onSubmit(c.ref, undefined))}
              busy={busy}
            />
            {scan.characteristic_options.length === 0 && (
              <p className="text-xs text-rose-600">
                1С не вернула ни одного варианта — уточнить нечем.
              </p>
            )}
          </div>
        </div>
      )}

      {scan.needs_partia && (
        <div>
          <div className="mb-1.5 text-xs font-semibold text-slate-600">Партия</div>
          <input
            autoFocus={!scan.needs_characteristic}
            className="w-full rounded border p-2 text-sm"
            placeholder="напр. 126"
            value={batch}
            onChange={(e) => setBatch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && ready) {
                e.preventDefault();
                onSubmit(char || undefined, batch.trim());
              }
            }}
          />
          <Button
            className="mt-3 w-full"
            disabled={!ready}
            onClick={() => onSubmit(char || undefined, batch.trim())}
          >
            Принять
          </Button>
          {/* Партия в 1С может не найтись — тупика быть не должно. */}
          <Button
            variant="ghost"
            className="mt-1 w-full"
            disabled={scan.needs_characteristic && !char}
            onClick={() => onSkipPartia(char || undefined)}
          >
            Принять без партии
          </Button>
        </div>
      )}
    </BottomSheet>
  );
}
